import { useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Modal, Btn, Field, inputCls } from "@/components/common";
import { Camera, Loader2, Sparkles } from "lucide-react";

// Legge un file (foto o PDF) del passaporto, invia all'AI, mostra un'anteprima
// editabile dei dati letti e applica solo dopo conferma dell'utente.
export default function PassportScanner({ onResult, compact }) {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null); // { data, dataUrl }

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      setLoading(true);
      try {
        const { data } = await api.post("/passport/scan", { image_base64: dataUrl });
        setPreview({ data: { ...data }, dataUrl });
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Scansione non riuscita");
      } finally {
        setLoading(false);
        if (inputRef.current) inputRef.current.value = "";
      }
    };
    reader.readAsDataURL(file);
  };

  const setField = (k, v) => setPreview((p) => ({ ...p, data: { ...p.data, [k]: v } }));

  const confirm = () => {
    const d = preview.data;
    const full_name = `${(d.given_names || "").trim()} ${(d.surname || "").trim()}`.trim() || d.full_name || "";
    onResult({ ...d, full_name }, preview.dataUrl);
    setPreview(null);
    toast.success("Dati passaporto compilati");
  };

  const isPdf = preview?.dataUrl?.startsWith("data:application/pdf");

  return (
    <div className={compact ? "" : "rounded-md border border-dashed border-amber-300 bg-amber-50/60 p-3"}>
      {!compact && (
        <div className="flex items-center gap-2 mb-1 text-sm font-semibold text-amber-800">
          <Sparkles size={15} /> Compilazione automatica AI
        </div>
      )}
      {!compact && (
        <p className="text-xs text-amber-700/80 mb-2">
          Carica una foto o un PDF del passaporto: l'AI legge i campi e potrai correggerli prima di salvare.
        </p>
      )}
      <input ref={inputRef} type="file" accept="image/*,application/pdf,.pdf,.heic,.heif" className="hidden" onChange={handleFile} data-testid="passport-input" />
      <button
        type="button"
        data-testid="scan-passport-btn"
        disabled={loading}
        onClick={() => inputRef.current?.click()}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white border border-amber-300 text-amber-800 text-sm font-semibold hover:bg-amber-100 transition-colors duration-150 disabled:opacity-60"
      >
        {loading ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />}
        {loading ? "Lettura in corso…" : "Scansiona passaporto"}
      </button>

      <Modal open={!!preview} onClose={() => setPreview(null)} title="Verifica dati passaporto" wide>
        {preview && (
          <div className="space-y-4" data-testid="pp-preview">
            <p className="text-xs text-slate-500">Controlla e correggi i dati letti dall'AI, poi conferma.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Cognome">
                <input value={preview.data.surname || ""} onChange={(e) => setField("surname", e.target.value)} className={inputCls} data-testid="pp-surname" />
              </Field>
              <Field label="Nome">
                <input value={preview.data.given_names || ""} onChange={(e) => setField("given_names", e.target.value)} className={inputCls} data-testid="pp-given" />
              </Field>
              <Field label="N° passaporto">
                <input value={preview.data.passport_number || ""} onChange={(e) => setField("passport_number", e.target.value)} className={inputCls} data-testid="pp-passport" />
              </Field>
              <Field label="Nazionalità">
                <input value={preview.data.nationality || ""} onChange={(e) => setField("nationality", e.target.value)} className={inputCls} data-testid="pp-nationality" />
              </Field>
              <Field label="Data di nascita">
                <input type="date" value={preview.data.birth_date || ""} onChange={(e) => setField("birth_date", e.target.value)} className={inputCls} data-testid="pp-birth" />
              </Field>
              <Field label="Sesso">
                <select value={preview.data.sex || ""} onChange={(e) => setField("sex", e.target.value)} className={inputCls} data-testid="pp-sex">
                  <option value="">—</option>
                  <option value="M">M</option>
                  <option value="F">F</option>
                </select>
              </Field>
              <Field label="Data di scadenza">
                <input type="date" value={preview.data.expiry_date || ""} onChange={(e) => setField("expiry_date", e.target.value)} className={inputCls} data-testid="pp-expiry" />
              </Field>
              <Field label="Paese">
                <input value={preview.data.country || ""} onChange={(e) => setField("country", e.target.value)} className={inputCls} data-testid="pp-country" />
              </Field>
            </div>
            {preview.dataUrl && !isPdf && (
              <img src={preview.dataUrl} alt="documento" className="max-h-40 rounded border border-slate-200 object-contain" />
            )}
            {isPdf && <p className="text-xs text-slate-400">Anteprima non disponibile per i PDF.</p>}
            <div className="flex justify-end gap-2 pt-1">
              <Btn type="button" variant="secondary" onClick={() => setPreview(null)} data-testid="pp-cancel">Annulla</Btn>
              <Btn type="button" onClick={confirm} data-testid="pp-confirm">Conferma e compila</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
